import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { SeoProvider } from './contexts/SeoContext'
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider, useNotificationRefresh } from './contexts/NotificationContext'
import { initializeFirebaseMessagingForeground } from './services/firebaseMessagingForeground'
import { getAppBasePath, getServiceWorkerConfig } from './utils/serviceWorkerHelpers'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import StartupSplash from './components/StartupSplash'
import App from './App.jsx'
import './index.css'

// Componente para manejar mensajes del Service Worker (debe estar dentro de BrowserRouter)
function ServiceWorkerMessageHandler({ children }) {
  const navigate = useNavigate()
  const { refresh } = useNotificationRefresh()
  const basePath = getAppBasePath()

  useEffect(() => {
    // Registrar el Service Worker principal para PWA
    if ('serviceWorker' in navigator) {
      const sw = getServiceWorkerConfig()
      navigator.serviceWorker.register(sw.path, { scope: sw.scope })
        .then((registration) => {
          console.log('✅ Service Worker PWA registrado:', registration.scope)
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          }
          
          // Manejar actualizaciones del Service Worker
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('🔄 Nueva versión del Service Worker disponible')
                  registration.waiting?.postMessage({ type: 'SKIP_WAITING' })
                }
              })
            }
          })
        })
        .catch((error) => {
          console.error('❌ Error al registrar Service Worker PWA:', error)
        })

      // Inicializar listener de mensajes en foreground a nivel global.
      // Así funciona aunque el usuario no entre a Configuración.
      initializeFirebaseMessagingForeground()
    }

    // Escuchar mensajes del Service Worker
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        console.log('📨 Mensaje recibido del Service Worker:', event.data)

        if (event.data && event.data.type === 'PUSH_RECEIVED') {
          window.dispatchEvent(new CustomEvent('push-remote-received', { detail: event.data }))
          ;[700, 2000].forEach((delay) => {
            setTimeout(() => {
              refresh()
            }, delay)
          })
          return
        }
        
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          const { url, notificacionId } = event.data
          
          // Navegar a la URL especificada
          if (url) {
            const normalizedUrl =
              basePath !== '/' && url.startsWith(`${basePath}/`)
                ? url.slice(basePath.length)
                : url
            console.log('🔔 Navegando a:', normalizedUrl)
            navigate(normalizedUrl)
            
            // Refrescar notificaciones después de un pequeño delay
            setTimeout(() => {
              refresh()
            }, 500)
          }
        }
      }
      
      navigator.serviceWorker.addEventListener('message', handleMessage)
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage)
      }
    }
  }, [navigate, refresh])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined

    const handleControllerChange = () => {
      // Una sola recarga por sesión cuando entra un SW nuevo (evita loop con activate).
      const key = 'sw-controller-reloaded'
      if (sessionStorage.getItem(key)) return
      sessionStorage.setItem(key, '1')
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
    }
  }, [])

  useEffect(() => {
    // También escuchar cuando la ventana recibe foco (por si se abrió desde una notificación)
    const handleFocus = () => {
      // Refrescar notificaciones cuando la ventana recibe foco
      refresh()
    }
    
    window.addEventListener('focus', handleFocus)
    
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [refresh])

  return <>{children}</>
}

// Función para renderizar la app
function renderApp() {
  const rootElement = document.getElementById('root')
  
  // Reutilizar root existente si está disponible (para hot reload)
  if (rootElement._reactRootContainer) {
    return rootElement._reactRootContainer
  }
  
  const root = ReactDOM.createRoot(rootElement)
  rootElement._reactRootContainer = root
  const routerBasename = getAppBasePath()
  
  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <BrowserRouter basename={routerBasename}>
          <SeoProvider>
            <AuthProvider>
              <NotificationProvider>
                <ServiceWorkerMessageHandler>
                  <StartupSplash />
                  <App />
                  <PWAInstallPrompt />
                </ServiceWorkerMessageHandler>
              </NotificationProvider>
            </AuthProvider>
          </SeoProvider>
        </BrowserRouter>
      </ThemeProvider>
    </React.StrictMode>
  )
  
  return root
}

// Renderizar la app
renderApp()

